import * as Sentry from '@sentry/react';
import type {
  Projection,
  ProjectionsAndScoresAPIResponse
} from '@library/types/projection';
import {
  createProjectionsWebSocket,
  getProjectionsFromWebSocket
} from '@bo-utils/projection';
import {
  getPlayerModalProjections,
  isRelevantWSMessage
} from '@bo-utils/playerModal';
import type { GamePlayerProjectionTrading } from '@library/types/trading';
import type { PlayerModalProjectionsRequest } from '@library/types/playerModal';
import { api } from '@bo-services/api';
import { apiPaths } from '@bo-constants/services';
import { getTradingProjection } from '@bo-utils/trading';

const { admin } = apiPaths;

export const playerModalApi = api.injectEndpoints({
  endpoints: (build) => ({
    getPlayerModalProjections: build.query<
      Array<GamePlayerProjectionTrading>,
      PlayerModalProjectionsRequest
    >({
      query: ({
        durationId,
        foreignId,
        fromDate,
        gameId,
        leagueId,
        playerId,
        toDate
      }) => ({
        url: `${admin}projections`,
        method: 'GET',
        params: {
          duration_id: durationId,
          foreign_id: foreignId,
          from_date: fromDate,
          game_ids: gameId,
          league_id: leagueId,
          new_player_ids: playerId,
          to_date: toDate,
          page: 1,
          per_page: 100
        },
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/json; charset=UTF-8'
        }
      }),
      async onCacheEntryAdded(
        arg,
        { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
      ) {
        try {
          await cacheDataLoaded;

          const ws = createProjectionsWebSocket([arg.leagueId]);

          const listener = (event: MessageEvent) => {
            const { projections, included } =
              getProjectionsFromWebSocket(event);
            if (!projections.length || !included) return;

            projections.forEach((dataItem: Projection<string>) => {
              if (
                !isRelevantWSMessage({
                  arg,
                  projectionData: { data: dataItem, included }
                })
              ) {
                return;
              }

              const streamedProjection = getTradingProjection({
                projection: dataItem,
                included
              });

              updateCachedData((draft) => {
                if (!draft) {
                  return [streamedProjection];
                }

                const projectionIndex = draft.findIndex(
                  (projection) =>
                    projection.projectionId === streamedProjection.projectionId
                );

                if (projectionIndex === -1) {
                  draft.push(streamedProjection);
                } else {
                  draft[projectionIndex] = streamedProjection;
                }

                return draft;
              });
            });
          };

          ws.addEventListener('message', listener);

          await cacheEntryRemoved;
          ws.removeEventListener('message', listener);
          ws.close();
        } catch (error) {
          Sentry.captureException(error, {
            tags: {
              source: 'playerModalApi.getPlayerModalProjections WebSocket Error'
            },
            extra: { message: 'Player Modal Websocket Error' }
          });

          // eslint-disable-next-line no-console
          console.log('Player Modal Websocket Error', error);
        }
      },
      keepUnusedDataFor: 120,
      transformResponse: (response: ProjectionsAndScoresAPIResponse) =>
        getPlayerModalProjections(response)
    })
  })
});

export const { useGetPlayerModalProjectionsQuery } = playerModalApi;
